export const RFQ_CATEGORY_TOOL_NAME = "get_rfq_categories";
export const RFQ_CATEGORY_TIMEOUT_MS = 20_000;

const VALID_CATEGORY_KEYS = new Set(["name", "key", "term_id", "url"]);

function hasEmptyInput(input) {
  return !!input
    && typeof input === "object"
    && !Array.isArray(input)
    && Object.keys(input).length === 0;
}

function isValidCategory(category) {
  const isShapeValid = !!category
    && typeof category === "object"
    && !Array.isArray(category)
    && Object.keys(category).length === VALID_CATEGORY_KEYS.size
    && Object.keys(category).every(key => VALID_CATEGORY_KEYS.has(key))
    && typeof category.name === "string"
    && category.name.length > 0
    && typeof category.key === "string"
    && category.key.length > 0
    && Number.isInteger(category.term_id)
    && category.term_id > 0
    && typeof category.url === "string"
    && category.url.length > 0;

  if (!isShapeValid) return false;

  try {
    const url = new URL(category.url);
    return url.protocol === "https:"
      && url.searchParams.get("component_category") === String(category.term_id);
  } catch {
    return false;
  }
}

function isValidResponse(output, status) {
  return status === 200
    && !!output
    && output.success === true
    && Object.keys(output).length === 2
    && !!output.data
    && Object.keys(output.data).length === 1
    && Array.isArray(output.data.categories)
    && output.data.categories.every(isValidCategory);
}

function outputError(addToolOutput, toolCall, errorText) {
  return addToolOutput({
    tool: toolCall.toolName,
    toolCallId: toolCall.toolCallId,
    state: "output-error",
    errorText,
  });
}

export async function handleRfqCategoryToolCall({
  toolCall,
  endpointUrl,
  addToolOutput,
  fetchImpl = globalThis.fetch,
  AbortControllerImpl = globalThis.AbortController,
  setTimeoutImpl = globalThis.setTimeout,
  clearTimeoutImpl = globalThis.clearTimeout,
}) {
  if (toolCall?.toolName !== RFQ_CATEGORY_TOOL_NAME) return false;

  if (!hasEmptyInput(toolCall.input)) {
    await outputError(addToolOutput, toolCall, "The RFQ category request is invalid.");
    return true;
  }

  if (typeof endpointUrl !== "string" || !endpointUrl.trim()) {
    await outputError(addToolOutput, toolCall, "RFQ categories are unavailable.");
    return true;
  }

  const controller = new AbortControllerImpl();
  const timeoutId = setTimeoutImpl(() => controller.abort(), RFQ_CATEGORY_TIMEOUT_MS);

  try {
    const response = await fetchImpl(endpointUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    const output = await response.json();

    if (!isValidResponse(output, response.status)) {
      throw new Error("Invalid RFQ category response.");
    }

    await addToolOutput({
      tool: toolCall.toolName,
      toolCallId: toolCall.toolCallId,
      output,
    });
  } catch (error) {
    const timedOut = controller.signal.aborted || error?.name === "AbortError";
    await outputError(
      addToolOutput,
      toolCall,
      timedOut ? "The RFQ category lookup timed out." : "RFQ categories could not be loaded.",
    );
  } finally {
    clearTimeoutImpl(timeoutId);
  }

  return true;
}
